import React, { useState } from 'react';
import { useCallback } from 'react';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { CircularProgress, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { FormattedMessage } from 'react-intl';
import { delay } from 'utils/delay';

type NotesLoadMoreProps = {
  loading: boolean;
  cursor?: string | null;
  loadMore: (cursor: string) => void;
};

const useStyles = makeStyles((theme) => ({
  loading: {
    display: 'flex',
    width: '100%',
    justifyContent: 'center',
    padding: theme.spacing(1, 0)
  },
  loadingText: {
    paddingLeft: theme.spacing(1)
  }
}));

export const NotesLoadMore: React.FC<NotesLoadMoreProps> = ({
  loading,
  cursor,
  loadMore
}: NotesLoadMoreProps) => {
  const classes = useStyles();
  const [loadingMore, setLoadingMore] = useState(loading);
  const hasMore = Boolean(cursor);

  const onLoadMore = useCallback(async () => {
    if (cursor) {
      setLoadingMore(true);
      await loadMore(cursor);
      // so basically the infinity scroll bar hook will trigger onLoadMore if loading is false and loading circle is still visible,
      // due to some reason (proabably a bug in infinite scroll). it will endup triggering next service call two page loads in sinlge scroll
      await delay(20);
      setLoadingMore(false);
    }
  }, [loadMore, cursor]);

  const [sentryRef] = useInfiniteScroll({
    loading: loading || loadingMore,
    hasNextPage: hasMore,
    onLoadMore: onLoadMore,
    delayInMs: 10,
    rootMargin: '0px 0px 600px 0px'
  });

  return hasMore ? (
    <div
      ref={sentryRef}
      className={classes.loading}
      data-testid="notes-inifinite-scroll-loading"
    >
      <CircularProgress size={18} />
      <Typography variant="body2" className={classes.loadingText}>
        <FormattedMessage id="smartDiary.loading" />
      </Typography>
    </div>
  ) : (
    <></>
  );
};
